import {
  getDistanceUnitForCountry,
  type DistanceUnit,
} from "../countryMetadata";

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Haversine distance between two coordinates, in miles.
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Format distance for display.
 */
type DistanceUnitInput =
  | DistanceUnit
  | "kilometer"
  | "kilometers"
  | "kilometre"
  | "kilometres"
  | "kms"
  | "metric"
  | "mile"
  | "miles"
  | "imperial";

function normalizeDistanceUnit(
  unit: DistanceUnitInput | undefined | null,
): DistanceUnit {
  const normalized = unit?.toLowerCase();
  if (
    normalized === "km" ||
    normalized === "kilometer" ||
    normalized === "kilometers" ||
    normalized === "kilometre" ||
    normalized === "kilometres" ||
    normalized === "kms" ||
    normalized === "metric"
  ) {
    return "km";
  }
  return "mi";
}

export function formatDistance(
  distanceInMiles: number,
  unit: DistanceUnitInput = "mi",
): string {
  if (normalizeDistanceUnit(unit) === "km") {
    const km = distanceInMiles * 1.609344;
    if (km < 0.1) return "< 0.1 km";
    if (km < 1) return `${km.toFixed(1).replace(".", ",")} km`;
    return `${Math.round(km)} km`;
  }
  if (distanceInMiles < 0.1) return "Less than 0.1 mi";
  if (distanceInMiles < 1) return `${distanceInMiles.toFixed(1)} mi`;
  return `${Math.round(distanceInMiles)} mi`;
}

export function formatDistanceForCountry(
  distanceInMiles: number,
  country: string | undefined | null,
): string {
  return formatDistance(distanceInMiles, getDistanceUnitForCountry(country));
}
